import { Injectable, inject } from '@angular/core';
import { isEqual } from 'lodash';
import { error, log, warn } from '@jepto/shared/utils/logger';
import { ChartCardConfig } from '../models/chart-config.model';
import { CardConfig } from '../models/card-model';
import {
	ConfigChange,
	ChangeResult,
	ValidationWarning,
	WarningSeverity,
} from './chart-config-change.types';
import { ChartConfigValidator } from './chart-config-validator.service';
import { ChartDataMigrationService } from './chart-data-migration.service';
import { CardPersistenceService } from './card-persistence.service';
import { sanitizeCardDataConfigForGql } from '../utils/card-data-gql.util';
import { constrainAiChartDataForRequirements } from '../utils/ai-chart-type-requirements.util';
import { qualifyAiCardDataFields } from '../utils/qualify-ai-card-data-fields.util';

/**
 * Tracks which fields were changed in a patch.
 */
interface PatchChanges {
	chartTypeChanged: boolean;
	dataChanged: boolean;
	visualizationChanged: boolean;
	otherChanged: boolean;
}

/**
 * Central orchestrator for all chart configuration changes.
 *
 * All mutations flow through this service:
 * - Chart type changes (UI)
 * - AI patches
 * - Data tab changes
 * - Custom JSON edits
 *
 * Responsibilities:
 * 1. Diff patches against current config
 * 2. Run validation
 * 3. Migrate data when chart type changes
 * 4. Reset or merge visualization overrides
 * 5. Queue changes for persistence (auto-save)
 * 6. Return result with warnings
 */
@Injectable({
	providedIn: 'root',
})
export class ChartConfigChangeService {
	private validator = inject(ChartConfigValidator);
	private migration = inject(ChartDataMigrationService);
	private persistence = inject(CardPersistenceService);

	initializeCard(cardId: string, version: number | null): void {
		this.persistence.initializeCard(cardId, version);
	}

	/** @see CardConfigChangeService.bootstrapPersistenceTracking */
	bootstrapPersistenceTracking(
		cardId: string,
		revisions: number | null | undefined
	): void {
		const fromConfig =
			typeof revisions === 'number' && Number.isFinite(revisions)
				? revisions
				: null;
		const tracked = this.persistence.getCurrentRevisions(cardId);
		if (tracked === null) {
			this.persistence.initializeCard(cardId, fromConfig);
			return;
		}
		if (fromConfig !== null && fromConfig > tracked) {
			this.persistence.syncServerRevisions(cardId, fromConfig);
		}
	}

	applyChange(
		cardId: string,
		current: ChartCardConfig,
		change: ConfigChange
	): ChangeResult {
		const changes = this.detectChanges(current, change.patch);

		let prospective: ChartCardConfig = {
			...current,
			...change.patch,
		};

		const warnings: ValidationWarning[] = [];
		let migrated = false;

		let chartTypeChangeContext:
			| {
					fromType: string;
					toType: string;
					dataOutcome: 'migration' | 'unchanged';
			  }
			| undefined;

		if (changes.chartTypeChanged && change.patch.chartType) {
			const fromType = current.chartType;
			const toType = change.patch.chartType;
			let dataOutcome: 'migration' | 'unchanged' = 'unchanged';

			prospective = {
				...prospective,
				chartType: toType,
				visualization: {} as ChartCardConfig['visualization'],
			};

			if (this.validator.requiresMigration(fromType, toType)) {
				const migrationResult = this.migration.migrateData(
					current.data,
					fromType,
					toType
				);

				if (migrationResult.success) {
					prospective = {
						...prospective,
						data: migrationResult.data,
					};
					migrated = true;
					dataOutcome = 'migration';
				} else {
					error(
						'[ChartConfigChange] chart type change data migration failed',
						{
							cardId,
							fromType,
							toType,
							origin: change.origin,
							warnings: migrationResult.warnings,
						}
					);
				}

				warnings.push(...migrationResult.warnings);
			}

			chartTypeChangeContext = { fromType, toType, dataOutcome };
		}

		if (changes.dataChanged && change.patch.data) {
			const dataBase = changes.chartTypeChanged
				? migrated
					? (prospective.data ?? current.data ?? {})
					: (current.data ?? {})
				: (current.data ?? {});
			const patchData =
				change.origin === 'ai'
					? constrainAiChartDataForRequirements(
							qualifyAiCardDataFields(
								change.patch.data,
								change.patch.sources ??
									prospective.sources ??
									current.sources
							),
							prospective.chartType ?? current.chartType
						)
					: change.patch.data;
			prospective = {
				...prospective,
				data: { ...dataBase, ...(patchData as object) },
			};
		}

		if (changes.visualizationChanged && change.patch.visualization) {
			const useAiVisualizationNullDeletes = change.origin === 'ai';
			const visualizationBase = changes.chartTypeChanged
				? prospective.visualization
				: current.visualization;
			prospective = {
				...prospective,
				visualization: this.mergeVisualization(
					visualizationBase,
					change.patch.visualization,
					useAiVisualizationNullDeletes
				),
			};
		}

		if (change.patch.presentation) {
			prospective = {
				...prospective,
				presentation: this.deepMerge(
					(current.presentation ?? {}) as Record<string, unknown>,
					change.patch.presentation as Record<string, unknown>
				) as ChartCardConfig['presentation'],
			};
		}

		const validationWarnings = this.coalesceValidationWarnings(
			warnings,
			this.validator.validate(prospective)
		);
		warnings.push(...validationWarnings);

		const patchForPersistence = this.buildPatchForPersistence(
			change,
			changes,
			prospective,
			migrated
		);
		this.persistence.queueChange(cardId, patchForPersistence);

		if (chartTypeChangeContext) {
			this.logChartTypeChangeOutcome(
				cardId,
				chartTypeChangeContext,
				change.origin,
				migrated,
				warnings
			);
		}

		return {
			config: prospective,
			warnings,
			migrated,
		};
	}

	/**
	 * Derives the debounced autosave patch from the final config, not only the UI patch.
	 */
	private buildPatchForPersistence(
		change: ConfigChange,
		changes: PatchChanges,
		prospective: ChartCardConfig,
		migrated: boolean
	): Partial<ChartCardConfig> {
		if (changes.chartTypeChanged && change.patch.chartType) {
			const patch: Partial<ChartCardConfig> = {
				chartType: prospective.chartType,
				visualization: prospective.visualization ?? {},
			};
			const shouldPersistData =
				migrated ||
				change.patch.data !== undefined ||
				(changes.dataChanged && prospective.data !== undefined);
			if (shouldPersistData && prospective.data !== undefined) {
				patch.data = prospective.data;
			}
			return patch;
		}

		const patch: Partial<ChartCardConfig> = {};
		const incoming = change.patch;

		if (changes.dataChanged && prospective.data !== undefined) {
			patch.data = sanitizeCardDataConfigForGql(
				prospective.data as Record<string, unknown>
			) as ChartCardConfig['data'];
		}
		if (
			changes.visualizationChanged &&
			incoming.visualization !== undefined
		) {
			patch.visualization = incoming.visualization;
		}
		if (incoming.presentation) {
			patch.presentation = incoming.presentation;
		}
		if (incoming.palette !== undefined) {
			patch.palette = incoming.palette;
		}
		if (incoming.title !== undefined) {
			patch.title = incoming.title;
		}
		if (incoming.subtitle !== undefined) {
			patch.subtitle = incoming.subtitle;
		}
		if (incoming.style !== undefined) {
			patch.style = incoming.style;
		}
		if (incoming.icon !== undefined) {
			patch.icon = incoming.icon;
		}
		if (incoming.image !== undefined) {
			patch.image = incoming.image;
		}
		if (incoming.content !== undefined) {
			patch.content = incoming.content;
		}
		if (incoming.filterIds !== undefined) {
			patch.filterIds = incoming.filterIds;
		}

		if (Object.keys(patch).length > 0) {
			return patch;
		}

		return { ...incoming };
	}

	/**
	 * Drops validation warnings already covered by migration feedback on chart type change.
	 */
	private coalesceValidationWarnings(
		existing: ValidationWarning[],
		fromValidation: ValidationWarning[]
	): ValidationWarning[] {
		const codes = new Set(existing.map(w => w.code));
		return fromValidation.filter(w => {
			if (
				w.code === 'MISSING_TIME_DIMENSION' &&
				codes.has('NEEDS_TIME_DIMENSION')
			) {
				return false;
			}
			if (
				w.code === 'UNEXPECTED_TIME_DIMENSION' &&
				codes.has('REMOVED_TIME_DIMENSION')
			) {
				return false;
			}
			return true;
		});
	}

	private logChartTypeChangeOutcome(
		cardId: string,
		context: {
			fromType: string;
			toType: string;
			dataOutcome: 'migration' | 'unchanged';
		},
		origin: ConfigChange['origin'],
		migrated: boolean,
		warnings: ValidationWarning[]
	): void {
		const { fromType, toType, dataOutcome } = context;
		const label = `[ChartConfigChange] chart type change ${fromType} → ${toType}`;

		log(label, {
			cardId,
			origin,
			dataOutcome,
			migrated,
		});

		for (const warning of warnings) {
			if (warning.severity === WarningSeverity.Silent) {
				continue;
			}

			const payload = {
				code: warning.code,
				message: warning.message,
				field: warning.field,
				suggestedAction: warning.suggestedAction,
			};

			switch (warning.severity) {
				case WarningSeverity.Blocking:
					error(`${label} (blocking)`, payload);
					break;
				case WarningSeverity.Warning:
					warn(`${label} (warning)`, payload);
					break;
				case WarningSeverity.Info:
					log(`${label} (info)`, payload);
					break;
			}
		}
	}

	previewChange(
		current: ChartCardConfig,
		change: ConfigChange
	): ValidationWarning[] {
		return this.validator.validateChange(current, change);
	}

	private detectChanges(
		current: ChartCardConfig,
		patch: Partial<ChartCardConfig>
	): PatchChanges {
		return {
			chartTypeChanged:
				patch.chartType !== undefined &&
				patch.chartType !== current.chartType,
			dataChanged:
				patch.data !== undefined && !isEqual(patch.data, current.data),
			visualizationChanged:
				patch.visualization !== undefined &&
				!isEqual(patch.visualization, current.visualization),
			otherChanged: Object.keys(patch).some(
				key =>
					!['chartType', 'data', 'visualization'].includes(key) &&
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					!isEqual((patch as any)[key], (current as any)[key])
			),
		};
	}

	private mergeVisualization(
		current: ChartCardConfig['visualization'],
		incoming: ChartCardConfig['visualization'],
		useAiNullDeletes: boolean
	): ChartCardConfig['visualization'] {
		if (!incoming) {
			return current;
		}

		if (!current) {
			if (useAiNullDeletes) {
				return this.deepMergeWithNullDeletes(
					{},
					incoming as Record<string, unknown>
				) as ChartCardConfig['visualization'];
			}
			return incoming;
		}

		const incomingRecord = incoming as Record<string, unknown>;

		if (Object.keys(incomingRecord).length === 0) {
			return {} as ChartCardConfig['visualization'];
		}

		if (useAiNullDeletes) {
			return this.deepMergeWithNullDeletes(
				{ ...(current as Record<string, unknown>) },
				incomingRecord
			) as ChartCardConfig['visualization'];
		}

		return this.deepMerge(
			current as Record<string, unknown>,
			incomingRecord
		) as ChartCardConfig['visualization'];
	}

	private deepMergeWithNullDeletes(
		base: Record<string, unknown>,
		incoming: Record<string, unknown>
	): Record<string, unknown> {
		const result: Record<string, unknown> = { ...base };

		for (const key of Object.keys(incoming)) {
			const incomingValue = incoming[key];

			if (incomingValue === null) {
				delete result[key];
				continue;
			}

			const baseValue = result[key];

			if (
				baseValue !== null &&
				typeof baseValue === 'object' &&
				!Array.isArray(baseValue) &&
				typeof incomingValue === 'object' &&
				incomingValue !== null &&
				!Array.isArray(incomingValue)
			) {
				result[key] = this.deepMergeWithNullDeletes(
					baseValue as Record<string, unknown>,
					incomingValue as Record<string, unknown>
				);
			} else {
				result[key] = incomingValue;
			}
		}

		return result;
	}

	private deepMerge(
		base: Record<string, unknown>,
		incoming: Record<string, unknown>
	): Record<string, unknown> {
		const result: Record<string, unknown> = { ...base };

		for (const key of Object.keys(incoming)) {
			const baseValue = base[key];
			const incomingValue = incoming[key];

			if (
				baseValue !== null &&
				typeof baseValue === 'object' &&
				!Array.isArray(baseValue) &&
				incomingValue !== null &&
				typeof incomingValue === 'object' &&
				!Array.isArray(incomingValue)
			) {
				result[key] = this.deepMerge(
					baseValue as Record<string, unknown>,
					incomingValue as Record<string, unknown>
				);
			} else {
				result[key] = incomingValue;
			}
		}

		return result;
	}

	async flush(cardId: string): Promise<void> {
		await this.persistence.flush(cardId);
	}

	async restoreVersion(
		cardId: string,
		targetVersion: number
	): Promise<CardConfig> {
		return await this.persistence.restoreVersion(cardId, targetVersion);
	}

	async undo(cardId: string): Promise<CardConfig> {
		const currentRevisions = this.persistence.getCurrentRevisions(cardId);
		if (currentRevisions === null || currentRevisions <= 1) {
			throw new Error('Cannot undo: no previous version exists');
		}
		const targetVersion = currentRevisions - 1;
		return await this.persistence.restoreVersion(cardId, targetVersion);
	}

	async undoSteps(cardId: string, steps: number): Promise<CardConfig> {
		const currentRevisions = this.persistence.getCurrentRevisions(cardId);
		if (currentRevisions === null) {
			throw new Error('Cannot undo: Invalid version number');
		}
		if (steps >= currentRevisions) {
			throw new Error(
				`Cannot undo ${steps} steps: only ${currentRevisions - 1} previous versions exist`
			);
		}
		const targetVersion = currentRevisions - steps;
		return await this.persistence.restoreVersion(cardId, targetVersion);
	}

	getCurrentRevisions(cardId: string): number | null {
		return this.persistence.getCurrentRevisions(cardId);
	}

	hasPendingChanges(cardId: string): boolean {
		return this.persistence.hasPendingChanges(cardId);
	}

	getPersistenceStatus(
		cardId: string
	): 'idle' | 'saving' | 'conflict' | 'offline' | 'error' {
		return this.persistence.getStatus(cardId);
	}

	onPersistenceStateChange(
		callback: (event: {
			cardId: string;
			previousStatus: string;
			currentStatus: string;
			revisions?: number;
		}) => void
	): () => void {
		return this.persistence.onStateChange(event => {
			callback({
				cardId: event.cardId,
				previousStatus: event.previousStatus,
				currentStatus: event.currentStatus,
				revisions: event.revisions,
			});
		});
	}

	onChangePersisted(
		callback: (event: {
			cardId: string;
			revisions: number;
			updatedAt: string;
		}) => void
	): () => void {
		return this.persistence.onChangePersisted(event => {
			callback({
				cardId: event.cardId,
				revisions: event.revisions,
				updatedAt: event.updatedAt,
			});
		});
	}

	cleanup(cardId: string): void {
		this.persistence.cleanup(cardId);
	}
}
